// per docs/shape-ops-block-levels.md §2.4 Exposure
// CF-48: a storage location and a system credential carry two independent
// blocks. A viewer's own switch sets and lifts its own block only; the other
// level's block is shown by name and never offered for lifting
// (TC-UC30-06-03, TC-UC31-06-03, TC-UC21-08-03).

export type BlockViewer = 'SYSTEM_ADMIN' | 'REGISTRAR';

export interface Blockable {
  active: boolean;
  blockedBySystemAdmin: boolean;
  blockedByRegistrar: boolean;
}

// What a status tag says. `inactive` is the viewer's own block, the one its
// switch lifts; the other two name a block by who set it.
export type BlockState = 'active' | 'inactive' | 'blocked_by_system_admin' | 'blocked_by_registrar';

export function blockViewerOf(activeRole: string | undefined): BlockViewer {
  return activeRole === 'SYSTEM_ADMIN' ? 'SYSTEM_ADMIN' : 'REGISTRAR';
}

/** The block the viewer's own switch shows and changes. */
export function ownBlocked(item: Blockable, viewer: BlockViewer): boolean {
  return viewer === 'SYSTEM_ADMIN' ? item.blockedBySystemAdmin : item.blockedByRegistrar;
}

/** The block of the other level, which the viewer can neither lift nor work around. */
export function otherBlocked(item: Blockable, viewer: BlockViewer): boolean {
  return viewer === 'SYSTEM_ADMIN' ? item.blockedByRegistrar : item.blockedBySystemAdmin;
}

/**
 * One state per block that is set, the System Administrator's first. The System
 * Administrator reads each block by who set it (TC-UC21-08-03); the Registrar
 * reads its own as "inactive" (TC-UC31-06-01) and the System Administrator's by
 * name (TC-UC21-08-01). An inactive row carrying neither flag predates them.
 */
export function blockStates(item: Blockable, viewer: BlockViewer): BlockState[] {
  if (item.active) return ['active'];
  const states: BlockState[] = [];
  if (item.blockedBySystemAdmin) states.push('blocked_by_system_admin');
  if (item.blockedByRegistrar) {
    states.push(viewer === 'SYSTEM_ADMIN' ? 'blocked_by_registrar' : 'inactive');
  }
  return states.length > 0 ? states : ['inactive'];
}
